import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen t-bg-page t-text py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/login" className="t-text-accent hover:opacity-80 text-sm t-focus-ring rounded">
            ← Back
          </Link>
          <h1 className="text-3xl font-bold t-text mt-4 mb-1">Privacy Policy</h1>
          <p className="t-text-disabled text-sm">Last updated: 14 April 2026</p>
        </div>

        <div className="space-y-10 text-sm leading-relaxed">

          <section aria-labelledby="s1">
            <h2 id="s1" className="text-lg font-semibold t-text mb-3">1. Who we are</h2>
            <p>
              That Music Game (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) operates{' '}
              <strong className="t-text">thatmusicgame.com</strong>. We are the data controller
              responsible for your personal data under the UK General Data Protection Regulation
              (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p className="mt-2">
              <strong className="t-text">Contact:</strong>{' '}
              <a
                href="mailto:privacy@thatmusicgame.com"
                className="t-text-accent hover:opacity-80 underline t-focus-ring rounded"
              >
                privacy@thatmusicgame.com
              </a>
            </p>
          </section>

          <section aria-labelledby="s2">
            <h2 id="s2" className="text-lg font-semibold t-text mb-3">2. Data we collect and why</h2>

            <h3 className="font-medium t-text-secondary mb-2">Account and authentication</h3>
            <ul className="list-disc list-inside space-y-1.5 mb-4">
              <li>
                <strong className="t-text">Email address</strong> - collected when you create an account.
                Used only for sign-in and essential account communications (e.g. password reset).
                Not displayed to other players.
              </li>
              <li>
                <strong className="t-text">Username</strong> - chosen by you. Visible to all
                authenticated players on the platform.
              </li>
              <li>
                <strong className="t-text">Google display name</strong> - collected during
                Google sign-in. Stored internally for reference only; never shown to other players.
              </li>
            </ul>
            <p className="t-text-muted mb-6">
              <strong className="t-text-secondary">Legal basis:</strong> Performance of a contract
              (UK GDPR Art. 6(1)(b)) - necessary to create and manage your account.
            </p>

            <h3 className="font-medium t-text-secondary mb-2">Game data</h3>
            <ul className="list-disc list-inside space-y-1.5 mb-4">
              <li>
                <strong className="t-text">Game history</strong> - rounds played, songs chosen,
                scores, and predictions.
              </li>
              <li>
                <strong className="t-text">Statistics</strong> - games played, rounds won, best
                friend (the player you predicted most accurately), favourite song.
              </li>
              <li>
                <strong className="t-text">Avatar</strong> - the emoji avatar you choose within
                the app.
              </li>
            </ul>
            <p className="t-text-muted mb-2">
              <strong className="t-text-secondary">Legal basis:</strong> Performance of a contract
              (UK GDPR Art. 6(1)(b)).
            </p>
            <p className="t-text-muted mb-6">
              Your game history and statistics are <strong className="t-text-secondary">private by default</strong>.
              You can make them visible to other signed-in users in your profile settings.
            </p>

            <h3 className="font-medium t-text-secondary mb-2">Payments</h3>
            <p className="mb-4">
              If you purchase Premium access, you will be redirected to Stripe&apos;s hosted checkout
              page. We receive confirmation of payment status from Stripe and store a Stripe customer
              identifier against your account. We do not store your card details - these are processed
              and stored by Stripe under their own privacy policy.
            </p>
            <p className="t-text-muted">
              <strong className="t-text-secondary">Legal basis:</strong> Performance of a contract
              (UK GDPR Art. 6(1)(b)).
            </p>
          </section>

          <section aria-labelledby="s3">
            <h2 id="s3" className="text-lg font-semibold t-text mb-3">3. Third-party services</h2>
            <p className="mb-4">
              We use the following third-party services to operate this platform. Each acts as a data
              processor under a data processing agreement, or as an independent data controller where noted.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="t-border border-b">
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Service</th>
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Purpose</th>
                    <th className="text-left py-2 t-text-secondary font-medium">Privacy policy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--c-border)">
                  <tr>
                    <td className="py-2 pr-4 t-text font-medium align-top">Google Firebase<br /><span className="t-text-muted font-normal">(Google LLC)</span></td>
                    <td className="py-2 pr-4 t-text-secondary align-top">Authentication, database, cloud hosting, and server-side processing</td>
                    <td className="py-2 align-top">
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                        policies.google.com/privacy
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 t-text font-medium align-top">Stripe Inc.</td>
                    <td className="py-2 pr-4 t-text-secondary align-top">Payment processing for Premium subscriptions (hosted checkout - no payment data touches our servers)</td>
                    <td className="py-2 align-top">
                      <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                        stripe.com/gb/privacy
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 t-text font-medium align-top">LiveKit Inc.</td>
                    <td className="py-2 pr-4 t-text-secondary align-top">Real-time video, audio, and screen-share relay during live game sessions (SFU infrastructure — media streams are routed through LiveKit&rsquo;s servers but are not stored)</td>
                    <td className="py-2 align-top">
                      <a href="https://livekit.io/legal/privacy" target="_blank" rel="noopener noreferrer" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                        livekit.io/legal/privacy
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4 t-text-muted">
              Your account and game data is stored in Google Firebase in the{' '}
              <strong className="t-text-secondary">europe-west2 region (London, UK)</strong> - within
              the United Kingdom. Google Firebase operates under a Data Processing Agreement that
              meets UK GDPR requirements.
            </p>
          </section>

          <section aria-labelledby="s4">
            <h2 id="s4" className="text-lg font-semibold t-text mb-3">4. Cookies and local storage</h2>

            <h3 className="font-medium t-text-secondary mb-2">Essential storage (no consent required)</h3>
            <p className="t-text-muted mb-3">
              These are required for the service to function and cannot be declined.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="t-border border-b">
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Name / key</th>
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Type</th>
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Purpose</th>
                    <th className="text-left py-2 t-text-secondary font-medium">Expiry</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--c-border)">
                  <tr>
                    <td className="py-2 pr-4 font-mono t-text-secondary align-top">Firebase Auth tokens</td>
                    <td className="py-2 pr-4 t-text-muted align-top">localStorage / IndexedDB</td>
                    <td className="py-2 pr-4 t-text-muted align-top">Keeps you signed in between sessions</td>
                    <td className="py-2 t-text-muted align-top">Until sign-out or token expiry</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono t-text-secondary align-top">lobby_join_rl</td>
                    <td className="py-2 pr-4 t-text-muted align-top">localStorage</td>
                    <td className="py-2 pr-4 t-text-muted align-top">Prevents brute-force game code attempts (rate limiting)</td>
                    <td className="py-2 t-text-muted align-top">Up to 15 minutes</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono t-text-secondary align-top">cookie_consent</td>
                    <td className="py-2 pr-4 t-text-muted align-top">localStorage</td>
                    <td className="py-2 pr-4 t-text-muted align-top">Stores your cookie preferences</td>
                    <td className="py-2 t-text-muted align-top">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-medium t-text-secondary mb-2">WebRTC (camera, microphone, screen share)</h3>
            <p className="t-text-muted">
              During a game, players may optionally enable their camera or microphone, and the host may
              share their screen so others can see and hear the music being played. Real-time media is
              relayed via <strong className="t-text-secondary">LiveKit Cloud</strong> (a third-party
              Selective Forwarding Unit service) — streams are routed through LiveKit&rsquo;s
              infrastructure for the duration of the session and are not stored. We do not record,
              retain, or process the content of your video, audio, or screen share on our own servers.
              Camera and microphone access is entirely opt-in and requires a one-time browser permission
              prompt on first use. No third-party cookies are set by this feature.
            </p>
          </section>

          <section aria-labelledby="s5">
            <h2 id="s5" className="text-lg font-semibold t-text mb-3">5. Data retention</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="t-border border-b">
                    <th className="text-left py-2 pr-4 t-text-secondary font-medium">Data</th>
                    <th className="text-left py-2 t-text-secondary font-medium">Retention period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--c-border)">
                  <tr>
                    <td className="py-2 pr-4 t-text-secondary align-top">Account data (username, email, avatar)</td>
                    <td className="py-2 t-text-muted align-top">Until you delete your account</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 t-text-secondary align-top">Game history and statistics</td>
                    <td className="py-2 t-text-muted align-top">Until you delete your account</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 t-text-secondary align-top">Payment records (Stripe customer ID, subscription status)</td>
                    <td className="py-2 t-text-muted align-top">As required by financial and legal obligations (typically 6 years)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section aria-labelledby="s6">
            <h2 id="s6" className="text-lg font-semibold t-text mb-3">6. Your rights</h2>
            <p className="mb-4">Under UK GDPR, you have the following rights:</p>
            <ul className="space-y-2">
              {[
                ['Access', 'Request a copy of the personal data we hold about you.'],
                ['Rectification', 'Ask us to correct inaccurate or incomplete data.'],
                ['Erasure', 'Ask us to delete your data ("right to be forgotten").'],
                ['Portability', 'Receive your data in a machine-readable format.'],
                ['Restriction', 'Ask us to limit how we use your data.'],
                ['Object', 'Object to processing based on legitimate interests.'],
                ['Withdraw consent', 'Withdraw cookie consent at any time - this does not affect prior lawful processing.'],
              ].map(([right, desc]) => (
                <li key={right} className="flex gap-2">
                  <span className="t-text-accent font-medium shrink-0">{right}:</span>
                  <span className="t-text-muted">{desc}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 t-text-muted">
              To exercise any of these rights, email{' '}
              <a href="mailto:privacy@thatmusicgame.com" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                privacy@thatmusicgame.com
              </a>
              . We will respond within 30 days.
            </p>
          </section>

          <section aria-labelledby="s7">
            <h2 id="s7" className="text-lg font-semibold t-text mb-3">7. Children</h2>
            <p>
              This service is not directed at children under 13. We do not knowingly collect personal
              data from children under 13. If you believe a child has provided us with their data,
              please contact us at{' '}
              <a href="mailto:privacy@thatmusicgame.com" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                privacy@thatmusicgame.com
              </a>{' '}
              and we will delete it promptly.
            </p>
          </section>

          <section aria-labelledby="s8">
            <h2 id="s8" className="text-lg font-semibold t-text mb-3">8. Changes to this policy</h2>
            <p>
              We will update this policy when our data practices change. The &ldquo;last updated&rdquo;
              date at the top of this page will reflect the most recent revision. We will notify you
              of material changes via the service.
            </p>
          </section>

          <section aria-labelledby="s9">
            <h2 id="s9" className="text-lg font-semibold t-text mb-3">9. Right to complain</h2>
            <p className="mb-2">
              If you are unhappy with how we handle your personal data, you have the right to lodge a
              complaint with the UK supervisory authority:
            </p>
            <div className="t-bg-surface t-border border rounded-lg p-4 text-sm">
              <p className="t-text font-medium">Information Commissioner&apos;s Office (ICO)</p>
              <p className="t-text-muted mt-1">
                Website:{' '}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="t-text-accent hover:opacity-80 underline t-focus-ring rounded">
                  ico.org.uk
                </a>
              </p>
              <p className="t-text-muted">Phone: 0303 123 1113</p>
            </div>
            <p className="mt-3 t-text-disabled text-xs">
              This policy applies to residents of the United Kingdom and the European Economic Area.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
